import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useMeta, pkr, waLink } from "@/lib/site";
import { Overline, Reveal } from "@/components/Reveal";
import { PostPurchaseReview } from "@/components/Reviews";

const reviewableSlugs = (items) => {
  const out = [];
  for (const i of items) {
    if (i.slug === "discovery-set") out.push("breeze", "diva", "elysian");
    else if (i.slug.startsWith("set-")) out.push(...i.slug.slice(4).split("+"));
    else if (i.slug.startsWith("decant-")) out.push(i.slug.slice(7));
    else if (i.slug.endsWith("-wax")) out.push(i.slug.slice(0, -4));
    else out.push(i.slug);
  }
  return [...new Set(out)];
};

const Input = ({ label, value, onChange, testid, textarea }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.25em] uppercase text-[#6E6151]">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={2}
        className="mt-1.5 w-full bg-[#DDD3C1] border border-[#2B2118]/10 p-3.5 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118]" />
    ) : (
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full bg-[#DDD3C1] border border-[#2B2118]/10 p-3.5 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118]" />
    )}
  </label>
);

const SAUDI_CITIES = ["jeddah", "jedda", "jiddah", "riyadh", "makkah", "mecca", "madinah", "medina", "dammam", "khobar", "dhahran", "taif", "jubail", "tabuk", "abha", "buraydah", "buraidah", "khamis mushait", "hail", "najran", "yanbu", "hofuf", "al ahsa", "qatif", "saudi", "ksa"];
const isSaudiCity = (city) => {
  const c = city.trim().toLowerCase();
  return c.length > 2 && SAUDI_CITIES.some((s) => c.includes(s));
};

export default function CartPage() {
  useMeta("Your Cart", "Review your Bonheur Eternal order — cash on delivery, all over Pakistan.");
  const { items, setQty, clear, subtotal } = useCart();
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", city: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [reviewables, setReviewables] = useState([]);
  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const placeOrder = async () => {
    if (!form.name || !form.phone || !form.address || !form.city) {
      toast.error("Please fill your name, phone, address and city.");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { ...form, items: items.map(({ slug, name, price, qty }) => ({ slug, name, price, qty })) });
      setReviewables(reviewableSlugs(items));
      setPlaced(data);
      clear();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place the order. Please try again.");
    }
    setPlacing(false);
  };

  if (placed) return (
    <div className="pt-32 md:pt-44 pb-32 px-5 md:px-8 max-w-2xl mx-auto text-center min-h-screen" data-testid="order-success">
      <PostPurchaseReview slugs={reviewables} buyerName={form.name} />
      <Reveal>
        <Overline>Order received</Overline>
        <h1 className="font-display text-4xl md:text-5xl text-[#2B2118] mt-4 tracking-tight">Thank you.</h1>
        <p className="mt-6 font-display text-2xl text-[#8F6B3A]" data-testid="order-number">{placed.order_number}</p>
        <p className="mt-6 text-[#5A4C3C] leading-loose">
          Your order of {pkr(placed.total)} has been received. We will confirm it — along with delivery details — by phone or WhatsApp within hours. Payment is cash on delivery.
          {placed.email_sent && " A confirmation email is on its way to your inbox."}
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a href={waLink(`Hello Bonheur Eternal, I just placed order ${placed.order_number} on your website. Looking forward to the confirmation.`)}
            target="_blank" rel="noopener noreferrer" data-testid="order-whatsapp-confirm"
            className="inline-flex items-center gap-2 rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors">
            <MessageCircle size={15} strokeWidth={1.5} /> Confirm on WhatsApp
          </a>
          <Link to="/collection" className="inline-flex items-center rounded-full border border-[#2B2118]/20 px-8 py-3.5 text-xs tracking-[0.2em] uppercase text-[#2B2118] hover:border-[#8F6B3A] transition-colors">
            Continue browsing
          </Link>
        </div>
      </Reveal>
    </div>
  );

  return (
    <div className="pt-32 md:pt-44 pb-32 px-5 md:px-8 max-w-6xl mx-auto min-h-screen">
      <Reveal>
        <Overline>Your Cart</Overline>
        <h1 className="font-display text-4xl sm:text-5xl text-[#2B2118] mt-4 tracking-tight">Almost yours.</h1>
      </Reveal>
      {items.length === 0 ? (
        <Reveal className="mt-16" data-testid="cart-empty">
          <p className="text-[#5A4C3C]">Your cart is empty — the collection is waiting.</p>
          <Link to="/collection" data-testid="cart-empty-cta" className="inline-block mt-6 text-xs tracking-[0.2em] uppercase text-[#8F6B3A] border-b border-[#8F6B3A]/50 pb-1 hover:text-[#2B2118] transition-colors">
            Explore the Collection
          </Link>
        </Reveal>
      ) : (
        <div className="grid lg:grid-cols-5 gap-14 mt-16">
          <div className="lg:col-span-3 divide-y divide-[#2B2118]/[0.06] border-y border-[#2B2118]/[0.06]" data-testid="cart-items">
            {items.map((i) => (
              <div key={i.slug} className="flex items-center gap-5 py-6" data-testid={`cart-item-${i.slug}`}>
                {i.image && <img src={i.image} alt={i.name} className="w-24 h-[75px] object-cover border border-[#2B2118]/[0.08]" />}
                <div className="flex-1">
                  <p className="font-display text-lg text-[#2B2118]">{i.name}</p>
                  <p className="text-xs font-medium text-[#5C4A33] mt-0.5">{pkr(i.price)}</p>
                </div>
                <div className="flex items-center gap-3 border border-[#2B2118]/10 px-3 py-1.5">
                  <button data-testid={`cart-dec-${i.slug}`} onClick={() => setQty(i.slug, i.qty - 1)} className="text-[#5A4C3C] hover:text-[#8F6B3A]"><Minus size={13} /></button>
                  <span className="text-sm w-4 text-center">{i.qty}</span>
                  <button data-testid={`cart-inc-${i.slug}`} onClick={() => setQty(i.slug, i.qty + 1)} className="text-[#5A4C3C] hover:text-[#8F6B3A]"><Plus size={13} /></button>
                </div>
                <p className="text-sm font-medium text-[#2B2118] w-24 text-right">{pkr(i.price * i.qty)}</p>
                <button data-testid={`cart-remove-${i.slug}`} onClick={() => setQty(i.slug, 0)} className="text-[#6E6151] hover:text-red-400"><X size={15} /></button>
              </div>
            ))}
            <div className="flex justify-between py-6">
              <p className="text-xs tracking-[0.25em] uppercase text-[#6E6151]">Subtotal</p>
              <p className="font-display text-2xl font-semibold text-[#6F5433]" data-testid="cart-subtotal">{pkr(subtotal)}</p>
            </div>
            <p className="py-4 text-xs text-[#6E6151] leading-relaxed">Cash on delivery, all over Pakistan. Delivery charges, if any, are confirmed with you before dispatch — never after.</p>
          </div>
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl text-[#2B2118] mb-8">Delivery details</h2>
            <div className="space-y-5">
              <Input label="Full name" value={form.name} onChange={set("name")} testid="checkout-name" />
              <Input label="Phone (for confirmation)" value={form.phone} onChange={set("phone")} testid="checkout-phone" />
              <Input label="Email (for your order confirmation, optional)" value={form.email} onChange={set("email")} testid="checkout-email" />
              <Input label="Delivery address" value={form.address} onChange={set("address")} testid="checkout-address" textarea />
              <Input label="City" value={form.city} onChange={set("city")} testid="checkout-city" />
              {isSaudiCity(form.city) && (
                <p data-testid="saudi-delivery-note" className="text-[11px] text-[#8F6B3A] leading-relaxed border border-[#8F6B3A]/25 bg-[#8F6B3A]/[0.06] px-3.5 py-2.5">
                  Delivering to Saudi Arabia — your parcel typically arrives in 10–15 working days.
                </p>
              )}
              <Input label="Notes (optional)" value={form.notes} onChange={set("notes")} testid="checkout-notes" />
            </div>
            <button data-testid="place-order-btn" onClick={placeOrder} disabled={placing}
              className="mt-8 w-full rounded-full bg-[#8F6B3A] text-[#FBF8F1] py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors disabled:opacity-60">
              {placing ? "Placing order…" : `Place order — Cash on Delivery`}
            </button>
            <p className="mt-4 text-[11px] text-[#6E6151] text-center">Prefer WhatsApp? <a href={waLink("Hello Bonheur Eternal, I'd like to place an order.")} target="_blank" rel="noopener noreferrer" className="text-[#8F6B3A] hover:underline">Order there instead</a>.</p>
          </div>
        </div>
      )}
    </div>
  );
}
